import styled, { css } from 'styled-components';
import { Swiper } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types';

import { Box } from '@/components/Box';
import {
  calcSwiperSlideBetweenByBreakpoints,
  calcSwiperSlideMaxWidth,
  calcSwiperSlideMaxWidthByBreakpoints,
  calcSwiperSlideSpaceBetween,
} from '@/components/Cms/Grid/helper';
import { Typography } from '@/components/Typography';

export const Wrapper = styled(Box)<{
  buttonOffset?: number;
  disableGutters?: boolean;
  isMobile?: boolean;
  minHeight: string;
  variantColor: 'light' | 'dark';
}>`
  width: 100%;
  ${({ minHeight }) => css`
    min-height: ${minHeight};
  `}
  position: relative;
  z-index: 1;

  > .swiper {
    position: unset !important;
    > .swiper-button {
      &-prev,
      &-next {
        display: none;
      }

      &-prev.swiper-button-disabled,
      &-next.swiper-button-disabled {
        display: none;
      }
    }
  }

  ${({ buttonOffset, disableGutters, isMobile, theme, variantColor }) => css`
    ${theme.media.md.down} {
      min-height: unset;
    }

    ${isMobile &&
    css`
      padding: 50px 0 !important;
    `}
    ${disableGutters &&
    css`
      ${theme.media.md.up} {
        padding-inline: 0;
      }
    `}

    ${theme.mediaQueries.md} {
      > .swiper {
        > .swiper-button {
          &-prev,
          &-next {
            margin-top: 0;
            color: ${variantColor === 'light'
              ? theme.colors.white
              : theme.colors.black};
            display: initial;
            top: ${buttonOffset ? buttonOffset + 'px' : '30%'};
            width: 40px;
            height: 40px;
            justify-content: center;
            align-items: center;
            display: inline-flex;
            //TODO need confirm
            // background-color: ${theme.colors.secondaryLightGrey};
            ::after {
              font-size: 24px;
            }
          }

          &-prev {
            left: -10px;
            text-align: left;
          }

          &-next {
            right: -10px;
            text-align: right;
          }
        }
      }
    }
  `}

  button[data-testid='productCardCTA'] {
    height: 2.5rem;
    white-space: normal;
    ${({ theme }) => theme.media.md.up} {
      display: inline-flex;
      white-space: nowrap;
    }
  }
`;

export const Title = styled(Typography)`
  text-transform: uppercase;
  ${({ theme }) => css`
    font-family: ${theme.fontFamilies.italianPlate};
    font-weight: ${theme.fontWeights.bold};
    font-size: ${theme.fontSizes.h4};
    padding-bottom: ${theme.spacing(3)};
  `}
`;

export const StyledSwiper = styled(Swiper)<{
  $hideNavigation: boolean;
  $itemsCount: number;
  $slidesPerView: number;
  $slidesPerViewForMobile: number | 'auto';
  breakpoints: SwiperOptions['breakpoints'];
  separator: string;
  slidesPerView: number | 'auto';
  spaceBetween?: string | number;
}>`
  > .swiper-wrapper {
    > .swiper-slide {
      ${({ breakpoints, slidesPerView, spaceBetween }) => css`
        ${calcSwiperSlideMaxWidth({ slidesPerView, spaceBetween })}
        ${calcSwiperSlideMaxWidthByBreakpoints(breakpoints)}
        ${calcSwiperSlideSpaceBetween({ spaceBetween })}
        ${calcSwiperSlideBetweenByBreakpoints(breakpoints)}
      `}
    }
  }

  ${({ $hideNavigation }) => css`
    ${$hideNavigation &&
    css`
      > .swiper-button-prev,
      > .swiper-button-next {
        display: none !important;
      }
    `}
  `}

  .swiper {
    ${({ separator, theme }) => css`
      &-slide {
        height: auto;

        ${theme.media.md.up} {
          &-visible {
            ::before {
              content: '';
              position: absolute;
              height: 130px;
              left: -55px;
              top: 35%;
              transform: translateY(-35%);
              width: 1px;
              background-color: ${theme.colors.defaultAdd5};
              display: ${separator === 'true' ? 'block' : 'none'};

              &:nth-child(n + 1) {
                width: 20px;
              }
            }

            ${theme.media.lg.up} {
              ::before {
                top: 32%;
                right: -60px;
                height: 140px;
              }
            }
          }

          &-active::before {
            display: none;
          }
        }
      }

      &-scrollbar {
        background: ${({ theme }) => theme.colors.mediumGrey};
        height: 2px;
        bottom: 0;
        width: 50%;
        left: 25%;

        ${theme.media.md.down} {
          width: 100%;
          left: 0;
        }

        &-drag {
          background: ${({ theme }) => theme.colors.primary};
        }
      }
    `}
  }

  ${({ $itemsCount, $slidesPerView, $slidesPerViewForMobile, theme }) => css`
    ${$itemsCount <= 2 &&
    css`
      .swiper-wrapper {
        transform: translateX(0);
      }
    `}
    ${theme.media.md.down} {
      ${typeof $slidesPerViewForMobile === 'number' &&
      $itemsCount < $slidesPerViewForMobile &&
      css`
        .swiper-wrapper {
          justify-content: center;
          transform: none !important;
        }
      `}
    }

    ${theme.media.md.up} {
      ${$itemsCount < $slidesPerView &&
      css`
        .swiper-wrapper {
          justify-content: center;
          transform: none !important;
        }
      `}
    }
  `}
`;
